import React from 'react';
import { useNavigate } from 'react-router-dom';

import { isDefined } from '../is';
import { Key } from '../keys';

interface Props {
  previousProductUrl: string | null;
  nextProductUrl: string | null;
  ref: React.RefObject<HTMLElement>;
  activeMediaIndex?: number;
  mediasLength?: number;
  onMediaIndexChange?: (index: number) => void;
  scrollRef?: React.RefObject<HTMLElement>;
}
export const usePdmArrowControls = ({
  previousProductUrl,
  nextProductUrl,
  ref,
  activeMediaIndex,
  mediasLength,
  onMediaIndexChange,
  scrollRef,
}: Props) => {
  const navigate = useNavigate();

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const tag = target.tagName;
      const parentTag = target.parentElement?.tagName ?? '';
      const isFormElement = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag);
      const isTableElement = ['TH', 'TD'].includes(parentTag);
      const shouldNotNavigate = isFormElement || isTableElement;

      const scrollElement = scrollRef?.current;
      const isWithinScrollElement = scrollElement && (scrollElement === target || scrollElement.contains(target));

      if (event.key === Key.LEFT && isDefined(previousProductUrl)) {
        if (shouldNotNavigate) return;
        event.preventDefault();
        navigate(previousProductUrl);
      }
      if (event.key === Key.RIGHT && isDefined(nextProductUrl)) {
        if (shouldNotNavigate) return;
        event.preventDefault();
        navigate(nextProductUrl);
      }
      if (
        !isWithinScrollElement &&
        typeof activeMediaIndex === 'number' &&
        isDefined(mediasLength) &&
        onMediaIndexChange &&
        (event.key === Key.UP || event.key === Key.DOWN)
      ) {
        event.preventDefault();

        const newIndex = event.key === Key.UP ? Math.max(activeMediaIndex - 1, 0) : Math.min(activeMediaIndex + 1, mediasLength - 1);

        if (newIndex !== activeMediaIndex) {
          onMediaIndexChange(newIndex);
        }
      }
    },
    [activeMediaIndex, mediasLength, navigate, nextProductUrl, onMediaIndexChange, previousProductUrl, scrollRef],
  );

  React.useEffect(() => {
    const element = ref.current;
    element?.focus();
    element?.addEventListener('keydown', handleKeyDown);

    return () => {
      element?.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref, handleKeyDown]);
};
